import { ComputerDesktopIcon, MinusIcon } from '@heroicons/react/24/outline';
import { Button, Spinner, Tooltip } from '@nextui-org/react';
import { Terminal } from '@xterm/xterm';
import React, { useEffect, useState, useCallback, useMemo, useLayoutEffect } from 'react';

import { PingStatus } from '@common/types';
import { ModernModal } from '@portal/components/common/ModernModal.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';
import { XTerm } from '@portal/components/XTerm';
import { useTerminalConnectionPool } from '@portal/context/TerminalConnectionPool';

// Ping Status Indicator Component
const PingStatusIndicator: React.FC<{ status: PingStatus }> = ({ status }) => {
  return (
    <div className='flex items-center gap-2 text-sm'>
      <span
        className={`w-3 h-3 rounded-full ${
          status.status === 'processing'
            ? 'bg-orange-500 animate-pulse'
            : status.status === 'connectable'
              ? 'bg-green-500'
              : 'bg-red-500'
        }`}
      />
      <span className='text-white'>
        {status.status === 'connectable' && status.avgPingTime
          ? `${status.avgPingTime.toFixed(2)}ms`
          : status.status === 'processing'
            ? 'Checking...'
            : 'Not Connectable'}
      </span>
    </div>
  );
};

export const ServerTerminal = () => {
  const {
    activeConnectionUUID,
    getActiveConnection,
    minimizeConnection,
    closeConnection,
    isModalOpen,
    setIsModalOpen,
    terminalRef,
    flushBufferToTerminal,
    isLoadingConnection,
  } = useTerminalConnectionPool();

  // Diagonal lines icon for resize handle
  const ResizeIcon = () => (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
      {/* Small box (original size) - top left */}
      <rect x='3' y='3' width='5' height='5' stroke='white' strokeWidth='1' fill='none' rx='0.5' opacity='0.8' />
      {/* Large box (resized) - bottom right */}
      <rect x='8' y='8' width='7' height='7' stroke='white' strokeWidth='1' fill='none' rx='0.5' opacity='0.8' />
      {/* Arrow from small to large box */}
      <path
        d='M8 8L11 11'
        stroke='white'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
        opacity='0.9'
      />
      {/* Arrow head */}
      <path
        d='M10 13L11 11L12 13'
        stroke='white'
        strokeWidth='1'
        strokeLinecap='round'
        strokeLinejoin='round'
        opacity='0.9'
      />
    </svg>
  );

  // Terminal size constraints
  const TERMINAL_CONSTRAINTS = {
    MIN_COLS: 90,
    MAX_COLS: 150,
    MIN_ROWS: 20,
    MAX_ROWS: 35,
  };

  const [termInit, setTermInit] = React.useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const terminalContainerRef = React.useRef<HTMLDivElement>(null);

  // NEW: Terminal measurement state for two-phase initialization
  const [terminalMeasured, setTerminalMeasured] = useState(false);
  const [measuredModalSize, setMeasuredModalSize] = useState({ width: 0, height: 0 });

  // Terminal dimensions state - this controls the terminal size
  const [terminalDimensions, setTerminalDimensions] = useState({
    cols: Math.max(TERMINAL_CONSTRAINTS.MIN_COLS, 80),
    rows: Math.max(TERMINAL_CONSTRAINTS.MIN_ROWS, 24),
  });

  // Pre-calculate the actual terminal size more accurately
  const calculateTerminalPixelSize = useCallback((cols: number, rows: number, fontSize = 14) => {
    const charWidth = fontSize * 0.6;
    const charHeight = fontSize + 2;
    const padding = 4;

    return {
      width: cols * charWidth + padding,
      height: rows * charHeight + padding,
    };
  }, []);

  const [actualTerminalSize, setActualTerminalSize] = useState(() => {
    const initialCols = Math.max(TERMINAL_CONSTRAINTS.MIN_COLS, 80);
    const initialRows = Math.max(TERMINAL_CONSTRAINTS.MIN_ROWS, 24);
    return calculateTerminalPixelSize(initialCols, initialRows);
  });

  const [terminalElementHeight, setTerminalElementHeight] = useState(0);
  const [terminalElementWidth, setTerminalElementWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartDimensions, setDragStartDimensions] = useState({
    cols: Math.max(TERMINAL_CONSTRAINTS.MIN_COLS, 80),
    rows: Math.max(TERMINAL_CONSTRAINTS.MIN_ROWS, 24),
  });
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });

  // Ping status and connection status states
  const [pingStatus, setPingStatus] = useState<PingStatus | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<string>('');

  const activeConnection = getActiveConnection();

  // Measure terminal and calculate modal size
  // Can be called from onInit (first time) or when connection changes (subsequent times)
  const measureTerminal = useCallback(() => {
    if (!terminalRef.current) return;

    requestAnimationFrame(() => {
      const terminalElement = document.getElementById('terminal');
      if (terminalElement) {
        const canvas = terminalElement.querySelector('canvas');
        const rect = canvas ? canvas.getBoundingClientRect() : terminalElement.getBoundingClientRect();

        const measuredWidth = rect.width;
        const measuredHeight = rect.height;

        setTerminalElementWidth(measuredWidth);
        setTerminalElementHeight(measuredHeight);

        // Calculate final modal size with all padding
        const modalPadding = 18;
        const headerHeight = 83;
        const terminalContainerPadding = 24;
        const terminalBottomPadding = 16;
        const scrollbarWidth = 24;
        const statusFooterHeight = connectionStatus ? 60 : 0; // Dynamic based on visibility

        const finalWidth = measuredWidth + modalPadding + terminalContainerPadding + scrollbarWidth;
        const finalHeight =
          measuredHeight +
          modalPadding +
          headerHeight +
          terminalContainerPadding +
          terminalBottomPadding +
          statusFooterHeight;

        const modalSize = {
          width: Math.max(600, Math.min(finalWidth, window.innerWidth - 100)),
          height: Math.max(400, Math.min(finalHeight, window.innerHeight - 100)),
        };

        // Lock in the measured size
        setMeasuredModalSize(modalSize);
        setTerminalMeasured(true);

        // Flush buffer after measurement completes
        setTimeout(() => {
          flushBufferToTerminal();
        }, 100);
      }
    });
  }, [flushBufferToTerminal, connectionStatus]);

  const onInit = (term: Terminal) => {
    terminalRef.current = term;
    setTermInit(true);

    // Ensure terminal is resized to the correct initial dimensions
    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (term.resizeTerminal) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        term.resizeTerminal(terminalDimensions.cols, terminalDimensions.rows);
      } else {
        term.resize(terminalDimensions.cols, terminalDimensions.rows);
      }

      // Calculate and set actual terminal size with the actual font size
      const fontSize = term.options.fontSize || 14;
      const actualSize = calculateTerminalPixelSize(terminalDimensions.cols, terminalDimensions.rows, fontSize);
      setActualTerminalSize(actualSize);

      // Measure terminal for modal sizing
      measureTerminal();
    } catch (error) {
      console.warn('Failed to resize terminal on init:', error);
    }
  };

  const onData = (data: string) => {
    if (activeConnection && activeConnection.websocket && activeConnection.websocket.readyState === 1) {
      activeConnection.websocket.send(data);
    }
  };

  // Terminal resize callback
  const handleTerminalResize = useCallback(
    (cols: number, rows: number) => {
      if (terminalRef.current) {
        const fontSize = terminalRef.current.options.fontSize || 14;
        const actualSize = calculateTerminalPixelSize(cols, rows, fontSize);
        setActualTerminalSize(actualSize);
      }
    },
    [calculateTerminalPixelSize]
  );

  // Simple modal size - use measured size, no complex recalculation
  const currentModalSize = terminalMeasured
    ? measuredModalSize
    : {
        width: 800,
        height: 600,
      };

  // Resize handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);
      setDragStartDimensions({ ...terminalDimensions });
      setDragStartPosition({ x: e.clientX, y: e.clientY });
    },
    [terminalDimensions]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const totalDragX = e.clientX - dragStartPosition.x;
      const totalDragY = e.clientY - dragStartPosition.y;

      const colsChange = Math.round(totalDragX / 8);
      const rowsChange = Math.round(totalDragY / 8);

      const newCols = Math.max(
        TERMINAL_CONSTRAINTS.MIN_COLS,
        Math.min(TERMINAL_CONSTRAINTS.MAX_COLS, dragStartDimensions.cols + colsChange)
      );
      const newRows = Math.max(
        TERMINAL_CONSTRAINTS.MIN_ROWS,
        Math.min(TERMINAL_CONSTRAINTS.MAX_ROWS, dragStartDimensions.rows + rowsChange)
      );

      if (newCols !== terminalDimensions.cols || newRows !== terminalDimensions.rows) {
        setTerminalDimensions({ cols: newCols, rows: newRows });
      }
    },
    [isDragging, dragStartDimensions, dragStartPosition, terminalDimensions]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Move terminal into modal when modal opens
  // Depends on both isModalOpen AND terminalMeasured to ensure modal has rendered before moving terminal
  useLayoutEffect(() => {
    const shouldShowModal = isModalOpen && terminalMeasured;

    if (shouldShowModal && terminalContainerRef.current) {
      const portalTarget = document.getElementById('terminal-portal-target');
      if (portalTarget && terminalContainerRef.current.parentElement !== portalTarget) {
        portalTarget.appendChild(terminalContainerRef.current);
        // Reset positioning for in-modal display and ensure full height
        terminalContainerRef.current.style.position = 'relative';
        terminalContainerRef.current.style.left = '0';
        terminalContainerRef.current.style.top = '0';
        terminalContainerRef.current.style.zIndex = '0';
        terminalContainerRef.current.style.pointerEvents = 'auto';
        terminalContainerRef.current.style.height = '100%';
        terminalContainerRef.current.style.width = '100%';
      }
    } else if (!shouldShowModal && terminalContainerRef.current) {
      // Move back to hidden position when modal closes
      const body = document.body;
      if (terminalContainerRef.current.parentElement !== body) {
        body.appendChild(terminalContainerRef.current);
      }
      // Hide off-screen
      terminalContainerRef.current.style.position = 'fixed';
      terminalContainerRef.current.style.left = '-10000px';
      terminalContainerRef.current.style.top = '0';
      terminalContainerRef.current.style.zIndex = '-1';
      terminalContainerRef.current.style.pointerEvents = 'none';
    }
  }, [isModalOpen, terminalMeasured]);

  // Handle terminal dimension changes (user manual resize)
  useEffect(() => {
    if (terminalRef.current && termInit && terminalMeasured) {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (terminalRef.current.resizeTerminal) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          terminalRef.current.resizeTerminal(terminalDimensions.cols, terminalDimensions.rows);
        } else {
          terminalRef.current.resize(terminalDimensions.cols, terminalDimensions.rows);
        }

        const fontSize = terminalRef.current.options.fontSize || 14;
        const actualSize = calculateTerminalPixelSize(terminalDimensions.cols, terminalDimensions.rows, fontSize);
        setActualTerminalSize(actualSize);

        // Recalculate modal size after user resize
        setTimeout(() => {
          const terminalElement = document.getElementById('terminal');
          if (terminalElement) {
            const canvas = terminalElement.querySelector('canvas');
            const rect = canvas ? canvas.getBoundingClientRect() : terminalElement.getBoundingClientRect();

            const measuredWidth = rect.width;
            const measuredHeight = rect.height;

            setTerminalElementWidth(measuredWidth);
            setTerminalElementHeight(measuredHeight);

            // Recalculate modal size
            const modalPadding = 18;
            const headerHeight = 83;
            const terminalContainerPadding = 24;
            const terminalBottomPadding = 16;
            const scrollbarWidth = 24;
            const statusFooterHeight = connectionStatus ? 60 : 0; // Dynamic based on visibility

            const finalWidth = measuredWidth + modalPadding + terminalContainerPadding + scrollbarWidth;
            const finalHeight =
              measuredHeight +
              modalPadding +
              headerHeight +
              terminalContainerPadding +
              terminalBottomPadding +
              statusFooterHeight;

            const newModalSize = {
              width: Math.max(600, Math.min(finalWidth, window.innerWidth - 100)),
              height: Math.max(400, Math.min(finalHeight, window.innerHeight - 100)),
            };

            setMeasuredModalSize(newModalSize);
          }
        }, 50);
      } catch (error) {
        console.warn('Failed to resize terminal:', error);
      }
    }
  }, [terminalDimensions, termInit, terminalMeasured, calculateTerminalPixelSize, connectionStatus]);

  const handleMinimize = () => {
    minimizeConnection();
    // Keep terminalMeasured = true, so modal can reopen instantly
  };

  const handleClose = () => {
    if (activeConnectionUUID) {
      closeConnection(activeConnectionUUID);
    }
    // terminalMeasured will be reset by useEffect when activeConnectionUUID becomes null
  };

  // Reset measurement state when connection closes
  useEffect(() => {
    if (!activeConnectionUUID) {
      // Connection closed, reset measurement for next connection
      setTerminalMeasured(false);
      setPingStatus(null);
      setConnectionStatus('');
    }
  }, [activeConnectionUUID]);

  // Listen for pingStatus and status WebSocket messages
  useEffect(() => {
    if (!activeConnection || !activeConnection.websocket) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const message = JSON.parse(event.data);

        if (message.eventType === 'pingStatus') {
          setPingStatus(message.data);
        } else if (message.eventType === 'status') {
          // Only show verbose status messages in the footer, not in status chip
          setConnectionStatus(message.data);
        } else if (message.eventType === 'success') {
          // Clear connection status when successfully connected
          setConnectionStatus('');
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    activeConnection.websocket.addEventListener('message', handleMessage);

    return () => {
      activeConnection.websocket.removeEventListener('message', handleMessage);
    };
  }, [activeConnection]);

  // Trigger measurement when a new connection opens and terminal is already initialized
  useEffect(() => {
    if (activeConnectionUUID && termInit && !terminalMeasured && isModalOpen) {
      // Terminal already initialized, but need to measure for new connection
      // Only measure when modal is supposed to be open
      measureTerminal();
    }
  }, [activeConnectionUUID, termInit, terminalMeasured, isModalOpen, measureTerminal]);

  // Dynamically adjust modal height when status footer shows/hides
  useEffect(() => {
    if (!terminalMeasured || !isModalOpen) return;

    const terminalElement = document.getElementById('terminal');
    if (!terminalElement) return;

    const canvas = terminalElement.querySelector('canvas');
    const rect = canvas ? canvas.getBoundingClientRect() : terminalElement.getBoundingClientRect();

    const measuredWidth = rect.width;
    const measuredHeight = rect.height;

    // Calculate modal size with dynamic status footer height
    const modalPadding = 18;
    const headerHeight = 83;
    const terminalContainerPadding = 24;
    const terminalBottomPadding = 16;
    const scrollbarWidth = 24;
    const statusFooterHeight = connectionStatus ? 60 : 0; // Dynamic based on visibility

    const finalWidth = measuredWidth + modalPadding + terminalContainerPadding + scrollbarWidth;
    const finalHeight =
      measuredHeight +
      modalPadding +
      headerHeight +
      terminalContainerPadding +
      terminalBottomPadding +
      statusFooterHeight;

    const newModalSize = {
      width: Math.max(600, Math.min(finalWidth, window.innerWidth - 100)),
      height: Math.max(400, Math.min(finalHeight, window.innerHeight - 100)),
    };

    setMeasuredModalSize(newModalSize);
  }, [connectionStatus, terminalMeasured, isModalOpen]);

  const onOpenChange = () => {
    // Toggle modal state
    setIsModalOpen(!isModalOpen);
  };

  // Get status chip color
  const getStatusColor = (): 'success' | 'warning' | 'danger' => {
    if (!activeConnection) return 'danger';
    switch (activeConnection.status) {
      case 'connected':
        return 'success';
      case 'connecting':
        return 'warning';
      default:
        return 'danger';
    }
  };

  // Calculate initial dimensions for terminal container
  const initialContainerSize = useMemo(
    () => calculateTerminalPixelSize(terminalDimensions.cols, terminalDimensions.rows),
    [] // Only calculate once on mount
  );

  // Don't render terminal at all if no connection has ever been opened
  // This prevents XTerm from initializing on app load and causing issues
  if (!activeConnectionUUID && !termInit) {
    return null;
  }

  return (
    <>
      {/* XTerm - Always mounted, moved into modal when open */}
      <div
        ref={terminalContainerRef}
        className='h-full w-full'
        style={{
          position: 'fixed',
          left: '-10000px',
          top: '0',
          zIndex: -1,
          pointerEvents: 'none',
          height: `${initialContainerSize.height}px`,
          width: `${initialContainerSize.width}px`,
        }}
      >
        <XTerm
          onData={onData}
          onInit={onInit}
          onResize={handleTerminalResize}
          initialCols={terminalDimensions.cols}
          initialRows={terminalDimensions.rows}
          keepTerminalMounted={true}
        />
      </div>

      {/* Global Loading Overlay - shown while measuring terminal, before modal opens */}
      {isModalOpen && !terminalMeasured && (
        <div className='fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50'>
          <div className='bg-gray-900 rounded-lg p-8 flex flex-col gap-4 items-center border border-gray-700'>
            <Spinner size='lg' />
            <TypographyUI variant='h6' className='text-white'>
              Initializing Terminal...
            </TypographyUI>
            <TypographyUI variant='caption' className='text-gray-400 text-center'>
              {activeConnection
                ? `Preparing connection to ${activeConnection.serverData.host}`
                : 'Setting up terminal...'}
            </TypographyUI>
          </div>
        </div>
      )}

      {/* Modal - only renders when terminal is measured */}
      {isModalOpen && terminalMeasured && (
        <ModernModal
          isOpen={true}
          onOpenChange={onOpenChange}
          title='Terminal'
          subtitle={
            activeConnection ? `${activeConnection.serverData.name}@${activeConnection.serverData.host}` : 'Loading...'
          }
          icon={<ComputerDesktopIcon className='h-4 w-4' />}
          statusChip={{
            text:
              activeConnection?.phase === 'ready'
                ? 'ready'
                : activeConnection?.phase === 'ready-hopping'
                  ? 'ready via hopping'
                  : activeConnection?.phase === 'initializing'
                    ? 'initializing'
                    : activeConnection?.phase === 'connecting'
                      ? 'connecting'
                      : activeConnection?.phase === 'proxying'
                        ? 'proxying'
                        : activeConnection?.phase === 'hopping'
                          ? 'hopping'
                          : activeConnection?.phase === 'authenticating'
                            ? 'authenticating'
                            : activeConnection?.phase === 'error'
                              ? 'error'
                              : 'disconnected',
            color: getStatusColor(),
          }}
          customHeaderActions={
            <div className='flex items-center gap-2'>
              {pingStatus && <PingStatusIndicator status={pingStatus} />}
              <Tooltip content='Minimize'>
                <Button
                  isIconOnly
                  variant='light'
                  size='sm'
                  onPress={handleMinimize}
                  className='text-white hover:bg-white/20 h-7 w-7 min-w-7'
                >
                  <MinusIcon className='h-4 w-4' />
                </Button>
              </Tooltip>
            </div>
          }
          onClose={handleClose}
          customSize={{
            width: currentModalSize.width,
            height: currentModalSize.height,
            maxWidth: '1200px',
            maxHeight: '800px',
            minWidth: '100px',
            minHeight: '100px',
          }}
        >
          <div ref={modalRef} className='relative h-full'>
            {/* Terminal Container */}
            <div className='bg-gray-900 rounded-lg border border-gray-700 h-full'>
              {/* Terminal Content */}
              <div className='h-full min-h-full relative' id='terminal-portal-target'>
                {/* Connection loading overlay - shown when switching connections */}
                {isLoadingConnection && (
                  <div className='absolute inset-0 bg-gray-900 flex flex-col gap-3 justify-center items-center z-20'>
                    <Spinner size='lg' />
                    <TypographyUI variant='h6' className='text-gray-300 text-center'>
                      Loading Connection...
                    </TypographyUI>
                    <TypographyUI variant='caption' className='text-gray-500 text-center'>
                      {activeConnection
                        ? `Switching to ${activeConnection.serverData.name}@${activeConnection.serverData.host}`
                        : 'Please wait...'}
                    </TypographyUI>
                  </div>
                )}
                {/* Terminal will be positioned here via Portal */}
              </div>
            </div>

            {/* Connection Info Footer */}
            {connectionStatus && (
              <div className='mt-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg'>
                <div className='flex items-center gap-2 text-xs text-gray-300'>
                  <Spinner size='sm' color='warning' />
                  <span>{connectionStatus}</span>
                </div>
              </div>
            )}

            {/* Resize Handle */}
            <div
              className='absolute bottom-[-24px] right-[-24px] w-6 h-6 cursor-se-resize bg-gray-500/70 hover:bg-gray-500/90 transition-colors rounded-tl z-10 flex items-center justify-center'
              onMouseDown={handleMouseDown}
            >
              <ResizeIcon />
            </div>
          </div>
        </ModernModal>
      )}
    </>
  );
};
